import { useState } from 'react';

const INITIAL_STATE = {
  name: '',
  phone: '',
  email: '',
  interests: '',
  userType: '',
};

const REQUIRED_FIELDS = ['name', 'phone','email', 'userType'];

// ─── Validators ───────────────────────────────────────────────────────────────

const validate = (fields) => {
  const errors = {};

  // Name — at least 2 characters
  if (!fields.name.trim()) {
    errors.name = 'Full name is required.';
  } else if (fields.name.trim().length < 2) {
    errors.name = 'Please enter your full name.';
  }

  // Phone — Australian and international formats
  const phoneDigits = fields.phone.replace(/\s|-/g, '');
  const phoneRegex = /^(\+?61|0)[2-9]\d{8}$|^\+?[1-9]\d{7,14}$/;
  if (!fields.phone.trim()) {
    errors.phone = 'Phone number is required.';
  } else if (!phoneRegex.test(phoneDigits)) {
    errors.phone = 'Please enter a valid phone number (e.g. 0412 345 678).';
  }

  // Email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!fields.email.trim()) {
    errors.email = 'Email address is required.';
  } else if (!emailRegex.test(fields.email.trim())) {
    errors.email = 'Please enter a valid email address.';
  }

  // User type
  if (!fields.userType) {
    errors.userType = 'Please select what describes you best.';
  }

  return errors;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

const useWaitlistForm = ({ onSubmit }) => {
  const [fields, setFields]           = useState(INITIAL_STATE);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched]         = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading]     = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const filledCount = REQUIRED_FIELDS.filter((k) => fields[k]).length;
  const progress    = Math.round((filledCount / REQUIRED_FIELDS.length) * 100);
  const isValid     = filledCount === REQUIRED_FIELDS.length;

  // Update field value and validate on change if already touched
  const setField = (key) => (e) => {
    const value = e.target.value;
    setFields((prev) => {
      const updated = { ...prev, [key]: value };
      if (touched[key]) {
        const errors = validate(updated);
        setFieldErrors((prev) => ({ ...prev, [key]: errors[key] }));
      }
      return updated;
    });
  };

  // Mark field as touched and validate on blur
  const setBlurred = (key) => () => {
    setTouched((prev) => ({ ...prev, [key]: true }));
    const errors = validate(fields);
    setFieldErrors((prev) => ({ ...prev, [key]: errors[key] }));
  };

  const handleSubmit = async () => {
    // Mark all fields touched so all errors show
    setTouched({ name: true, phone: true, email: true, userType: true });

    const errors = validate(fields);
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) return;

    setIsLoading(true);
    setSubmitError(null);

    //stays until page refresh
    try {
    await onSubmit({ ...fields, source: 'landing-page', status: 'active' });
    setIsSubmitted(true);

    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
      console.error('Waitlist submit error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    fields,
    setField,
    setBlurred,
    fieldErrors,
    isValid,
    isSubmitted,
    isLoading,
    submitError,
    progress,
    handleSubmit,
  };
};

export default useWaitlistForm;